// useHelpers.ts
export function useHelpers() {
  /** ========= Core small helpers you already had ========= **/
  function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  function isEmail(email: string) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function isUnder18(dateOfBirth: string | Date) {
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const md = today.getMonth() - birthDate.getMonth();
    if (md < 0 || (md === 0 && today.getDate() < birthDate.getDate())) age--;
    return age < 18;
  }

  function getFirstName(fullName: string) {
    const nameParts = (fullName || '').trim().split(/\s+/);
    return nameParts[0] || '';
  }

  function convertTimeToMeridian(timeString: string) {
    const [h, m] = timeString.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hh = h % 12 || 12;
    return `${hh}${period}`;
  }

  function matchWidths(selector: string) {
    const elements = document.querySelectorAll<HTMLElement>(selector);
    let maxWidth = 0;
    elements.forEach(el => (el.style.width = 'auto'));
    elements.forEach(el => { maxWidth = Math.max(maxWidth, el.offsetWidth); });
    elements.forEach(el => (el.style.width = `${maxWidth}px`));

    const handleResize = () => {
      elements.forEach(el => (el.style.width = 'auto'));
      let max = 0;
      elements.forEach(el => { max = Math.max(max, el.offsetWidth); });
      elements.forEach(el => (el.style.width = `${max}px`));
    };
    window.addEventListener('resize', handleResize);
  }

  function calculateStripeFees(amount: number) {
    const baseFee = amount;
    const stripePercentage = 0.0175;
    const fixedStripeFee = 0.30;
    const stripeFee = baseFee * stripePercentage + fixedStripeFee;
    const totalFee = baseFee + stripeFee;
    return {
      baseFee: baseFee.toFixed(2),
      stripeFee: stripeFee.toFixed(2),
      totalFee: totalFee.toFixed(2),
    };
  }

  function stripHtml(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim();
  }

  function truncate(text: string, max = 120): string {
    if (!text) return '';
    return text.length > max ? text.slice(0, max).trimEnd() + '…' : text;
  }

  function previewFromHtml(html: string, max = 120): string {
    return truncate(stripHtml(html), max);
  }

  /** ========= New: Body scroll lock (iOS/Android/Desktop safe) ========= **/
  const isIOS = /iP(ad|hone|od)/i.test(navigator.userAgent);

  let touchGuardsAttached = false;
  let removeTouchGuards: (() => void) | null = null;
  let savedScrollY = 0;
  // ✅ Narrow, click-safe scroll lock
  type ScrollLockOpts = {
    allow?: Element | string;    // the scrollable modal container
    backdrop?: Element | string; // the overlay element
  };
  let cleanupFns: Array<() => void> = [];   

  function attachTouchGuards(allowedEl?: Element | null) {
    if (touchGuardsAttached) return;

    const onTouchStart = (e: TouchEvent) => {
      if (!allowedEl) { e.preventDefault(); return; }
      if (!allowedEl.contains(e.target as Node)) e.preventDefault();
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!allowedEl) { e.preventDefault(); return; }
      if (!allowedEl.contains(e.target as Node)) { e.preventDefault(); return; }

      // prevent rubber-banding out of the scrollable area
      const t = allowedEl as HTMLElement;
      const canScroll = t.scrollHeight > t.clientHeight;
      if (!canScroll) { e.preventDefault(); return; }

      const top = t.scrollTop;
      const bottom = Math.ceil(top + t.clientHeight) >= t.scrollHeight;
      const currentY = e.touches[0].clientY;
      const lastY = (onTouchMove as any)._lastY ?? currentY;
      (onTouchMove as any)._lastY = currentY;

      const movingDown = currentY < lastY;
      if ((top <= 0 && !movingDown) || (bottom && movingDown)) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    removeTouchGuards = () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      (onTouchMove as any)._lastY = undefined;
      touchGuardsAttached = false;
    };

    touchGuardsAttached = true;
  }

  function detachTouchGuards() {
    removeTouchGuards?.();
    removeTouchGuards = null;
  }


 

  function disableScroll(opts: ScrollLockOpts = {}) {
    const html = document.documentElement;
    const body = document.body;

    // resolve elements
    const allowEl =
      typeof opts.allow === 'string'
        ? document.querySelector(opts.allow)
        : opts.allow ?? undefined;

    const backdropEl =
      typeof opts.backdrop === 'string'
        ? document.querySelector(opts.backdrop)
        : opts.backdrop ?? undefined;

    // 1) pin on iOS (no jumps when address bar hides)
    if (isIOS) {
      savedScrollY = window.scrollY || 0;
      body.style.position = 'fixed';
      body.style.top = `-${savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }

    // 2) block page scroll
    const oldW = html.clientWidth;
    html.style.overflow = 'hidden';
    (html.style as any).overscrollBehavior = 'contain';
    body.style.overflow = 'hidden';
    (body.style as any).overscrollBehavior = 'contain';
    // ⚠️ don't set touchAction:none on body (kills taps)

    // scrollbar compensation (desktop)
    const newW = html.clientWidth;
    const sbw = Math.max(0, newW - oldW);
    if (sbw) body.style.marginRight = `${sbw}px`;

    // 3) allow modal content to scroll smoothly on iOS
    if (allowEl instanceof HTMLElement) {
      (allowEl.style as any).WebkitOverflowScrolling = 'touch';
      (allowEl.style as any).overscrollBehavior = 'contain';
    }

    // 4) scope wheel/touch blockers ONLY to the backdrop
    //    (so clicks inside modal keep working)
    const blockWheel = (e: Event) => e.preventDefault();
    const blockTouchMove = (e: TouchEvent) => {
      // if move starts outside the allowed scroller OR scroller is at edge -> block
      if (!(allowEl instanceof HTMLElement)) { e.preventDefault(); return; }
      if (!allowEl.contains(e.target as Node)) { e.preventDefault(); return; }

      const t = allowEl;
      const canScroll = t.scrollHeight > t.clientHeight;
      if (!canScroll) { e.preventDefault(); return; }

      const top = t.scrollTop;
      const bottom = Math.ceil(top + t.clientHeight) >= t.scrollHeight;
      const y = e.touches[0].clientY;
      const last = (blockTouchMove as any)._lastY ?? y;
      (blockTouchMove as any)._lastY = y;
      const movingDown = y < last;
      if ((top <= 0 && !movingDown) || (bottom && movingDown)) e.preventDefault();
    };

    // attach to backdrop if provided; else attach to document but ONLY touchmove
    const target: EventTarget = backdropEl || document;
    target.addEventListener('wheel', blockWheel, { passive: false });
    target.addEventListener('touchmove', blockTouchMove, { passive: false });

    cleanupFns.push(() => {
      target.removeEventListener('wheel', blockWheel as any);
      target.removeEventListener('touchmove', blockTouchMove as any);
      (blockTouchMove as any)._lastY = undefined;
    });

    // pause smooth scroll libs
    (window as any).lenis?.stop?.();
    if ((window as any).fullpage_api) {
      const fp = (window as any).fullpage_api;
      fp.setAllowScrolling(false, 'all');
      fp.setKeyboardScrolling(false);
      fp.setFitToSection?.(false);
    }
  }

  function enableScroll() {
    const html = document.documentElement;
    const body = document.body;

    // unblock
    html.style.overflow = '';
    (html.style as any).overscrollBehavior = '';
    body.style.overflow = '';
    (body.style as any).overscrollBehavior = '';
    body.style.marginRight = '';

    // unpin iOS + restore scroll
    if (isIOS) {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      const y = savedScrollY || 0;
      savedScrollY = 0;
      window.scrollTo(0, y);
    }

    // detach blockers
    cleanupFns.forEach(fn => fn());
    cleanupFns = [];

    // resume libs
    (window as any).lenis?.start?.();
    if ((window as any).fullpage_api) {
      const fp = (window as any).fullpage_api;
      fp.setAllowScrolling(true, 'all');
      fp.setKeyboardScrolling(true);
      fp.setFitToSection?.(true);
    }
  }

  return {
    // existing
    delay,
    isEmail,
    isUnder18,
    getFirstName,
    convertTimeToMeridian,
    matchWidths,
    calculateStripeFees,
    stripHtml,
    truncate,
    previewFromHtml,

    // new
    disableScroll,
    enableScroll,
  };
}

export type HelpersType = ReturnType<typeof useHelpers>;